import AsyncStorage from '@react-native-async-storage/async-storage';

import { authService } from '@/services/auth-service';
import { buildDemoSeedData } from '@/services/demo-seed-data';
import { DuplicateWorkOrderError } from '@/services/errors';
import { getFormDataValue } from '@/types/form-data';
import {
	AttachmentType,
	AssociationStatus,
	AssociationType,
	LocationEventType,
	UserRole,
	WorkOrderPriority,
	WorkOrderStatus,
	type Association,
	type AssociationShortcutCode,
	type Attachment,
	type InspectionIssue,
	type InspectionReport,
	type InspectionReportIssue,
	type LocationAnalyticsEntry,
	type LocationEvent,
	type Material,
	type MaterialCost,
	type MiscCost,
	type Note,
	type TimeEntry,
	type User,
	type WorkOrder,
} from '@/types/models';
import type { WorkOrdersReportParams, WorkOrdersReportResponse } from '@/types/reports';
import { resolveCreateCreatedAt, resolveEditCreatedAt } from '@/utils/work-order-field-policy';

const LAST_LOCATION_KEY = 'lastKnownLocation';
const LOCAL_FILES_PREFIX = 'demo:files:';

export interface PaginatedResponse<T> {
	data: T[];
	total: number;
	page: number;
	limit: number;
	hasNext: boolean;
	hasPrev: boolean;
}

export interface QueryOptions {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	[key: string]: unknown;
}

type DemoCollectionName =
	| 'workOrders'
	| 'associations'
	| 'associationShortcutCodes'
	| 'users'
	| 'timeEntries'
	| 'materials'
	| 'materialCosts'
	| 'miscCosts'
	| 'notes'
	| 'attachments'
	| 'locationEvents'
	| 'inspectionReports'
	| 'inspectionIssues'
	| 'inspectionReportIssues';

type DemoCollectionMap = {
	workOrders: WorkOrder[];
	associations: Association[];
	associationShortcutCodes: AssociationShortcutCode[];
	users: User[];
	timeEntries: TimeEntry[];
	materials: Material[];
	materialCosts: MaterialCost[];
	miscCosts: MiscCost[];
	notes: Note[];
	attachments: Attachment[];
	locationEvents: LocationEvent[];
	inspectionReports: InspectionReport[];
	inspectionIssues: InspectionIssue[];
	inspectionReportIssues: InspectionReportIssue[];
};

type BaseRow = { id: string; createdAt: string; updatedAt: string; deletedAt?: string };

const DEMO_SEED_VERSION = '1';

const nowIso = (): string => new Date().toISOString();

const getNumber = (value: unknown): number | null => {
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string' && value.trim().length > 0) {
		const parsed = Number(value);
		if (Number.isFinite(parsed)) return parsed;
	}
	return null;
};

const getString = (value: unknown): string | null => {
	return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

const createId = (): string => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
};

const compareUnknown = (a: unknown, b: unknown): number => {
	if (a === b) return 0;
	if (a === null || a === undefined) return -1;
	if (b === null || b === undefined) return 1;

	if (typeof a === 'number' && typeof b === 'number') {
		return a - b;
	}
	if (typeof a === 'string' && typeof b === 'string') {
		return a.localeCompare(b);
	}

	// Fallback: compare stringified values
	return String(a).localeCompare(String(b));
};

const applyPagination = <T>(items: T[], options: QueryOptions): PaginatedResponse<T> => {
	const page = Math.max(1, getNumber(options.page) ?? 1);
	const limit = Math.max(1, getNumber(options.limit) ?? 50);
	const start = (page - 1) * limit;
	const end = start + limit;
	const slice = items.slice(start, end);

	return {
		data: slice,
		total: items.length,
		page,
		limit,
		hasNext: end < items.length,
		hasPrev: page > 1,
	};
};

export class DemoDataService {
	private seedPromise: Promise<void> | null = null;

	private async getSessionScopedPrefix(): Promise<string> {
		const session = await authService.getSession();
		if (!session) {
			throw new Error('No active session');
		}
		return `demo:${session.id}:`;
	}

	private async getStorageKey(name: string): Promise<string> {
		const prefix = await this.getSessionScopedPrefix();
		return `${prefix}${name}`;
	}

	private async readJson<T>(key: string, fallback: T): Promise<T> {
		const raw = await AsyncStorage.getItem(key);
		if (!raw) return fallback;
		try {
			return JSON.parse(raw) as T;
		} catch (error) {
			console.warn('DemoDataService: failed to parse storage JSON, resetting key', { key, error });
			await AsyncStorage.removeItem(key);
			return fallback;
		}
	}

	private async writeJson(key: string, value: unknown): Promise<void> {
		await AsyncStorage.setItem(key, JSON.stringify(value));
	}

	private async readCollection<K extends DemoCollectionName>(name: K): Promise<DemoCollectionMap[K]> {
		const key = await this.getStorageKey(`collection:${name}`);
		const value = await this.readJson<unknown>(key, []);
		if (!Array.isArray(value)) {
			return [] as DemoCollectionMap[K];
		}
		return value as DemoCollectionMap[K];
	}

	private async writeCollection<K extends DemoCollectionName>(name: K, items: DemoCollectionMap[K]): Promise<void> {
		const key = await this.getStorageKey(`collection:${name}`);
		await this.writeJson(key, items);
	}

	private async ensureSeeded(): Promise<void> {
		if (this.seedPromise) return this.seedPromise;
		this.seedPromise = this.ensureSeededInternal();
		return this.seedPromise;
	}

	private async ensureSeededInternal(): Promise<void> {
		const seedKey = await this.getStorageKey('seedVersion');
		const current = await AsyncStorage.getItem(seedKey);
		if (current === DEMO_SEED_VERSION) return;

		// Minimal seed: create the current demo user record so the UI can resolve user lookups.
		const session = await authService.getSession();
		if (!session) {
			throw new Error('No active session');
		}

		const ts = nowIso();
		const demoUser: User = {
			id: session.id,
			email: session.email,
			name: session.name,
			role: UserRole.Admin,
			isAdmin: true,
			organizationId: 'demo',
			createdAt: ts,
			updatedAt: ts,
		};

		const seed = buildDemoSeedData(demoUser);

		await this.writeCollection('users', seed.users);
		await this.writeCollection('associations', seed.associations);
		await this.writeCollection('associationShortcutCodes', seed.associationShortcutCodes);
		await this.writeCollection('workOrders', seed.workOrders);
		await this.writeCollection('timeEntries', seed.timeEntries);
		await this.writeCollection('materials', seed.materials);
		await this.writeCollection('materialCosts', seed.materialCosts);
		await this.writeCollection('miscCosts', seed.miscCosts);
		await this.writeCollection('notes', seed.notes);
		await this.writeCollection('attachments', seed.attachments);
		await this.writeCollection('locationEvents', seed.locationEvents);
		await this.writeCollection('inspectionReports', seed.inspectionReports);
		await this.writeCollection('inspectionIssues', seed.inspectionIssues);
		await this.writeCollection('inspectionReportIssues', seed.inspectionReportIssues);

		await AsyncStorage.setItem(seedKey, DEMO_SEED_VERSION);
	}

	private async getLastKnownLocation(): Promise<{ latitude: number; longitude: number } | null> {
		try {
			const stored = await AsyncStorage.getItem(LAST_LOCATION_KEY);
			if (!stored) return null;
			const parsed = JSON.parse(stored) as unknown;
			if (!parsed || typeof parsed !== 'object') return null;

			const raw = parsed as Record<string, unknown>;
			const latitude = raw.latitude;
			const longitude = raw.longitude;

			if (typeof latitude === 'number' && typeof longitude === 'number') {
				return { latitude, longitude };
			}

			const coords = raw.coords;
			if (coords && typeof coords === 'object') {
				const coordsRaw = coords as Record<string, unknown>;
				const coordsLat = coordsRaw.latitude;
				const coordsLng = coordsRaw.longitude;
				if (typeof coordsLat === 'number' && typeof coordsLng === 'number') {
					return { latitude: coordsLat, longitude: coordsLng };
				}
			}

			return null;
		} catch (error) {
			console.warn('DemoDataService: failed to load last known location', error);
			return null;
		}
	}

	private sortRows<T extends BaseRow>(rows: T[], options: QueryOptions): T[] {
		const sortBy = getString(options.sortBy) ?? 'updatedAt';
		const sortOrder = options.sortOrder === 'asc' ? 'asc' : 'desc';

		const sorted = [...rows].sort((a, b) => {
			const aRaw = a as unknown as Record<string, unknown>;
			const bRaw = b as unknown as Record<string, unknown>;
			const delta = compareUnknown(aRaw[sortBy], bRaw[sortBy]);
			return sortOrder === 'asc' ? delta : -delta;
		});

		return sorted;
	}

	private requireRow<T extends BaseRow>(rows: T[], id: string, label: string): T {
		const row = rows.find((candidate) => candidate.id === id && !candidate.deletedAt);
		if (!row) {
			throw new Error(`${label} not found`);
		}
		return row;
	}

	// Work Orders
	async getWorkOrders(options: QueryOptions = {}): Promise<PaginatedResponse<WorkOrder>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('workOrders')).filter((wo) => !wo.deletedAt);

		const associationId = getString(options.associationId);
		const statusRaw = getString(options.status);
		const search = getString(options.search) ?? getString(options.q);
		const assignedTo = getString(options.assignedTo);
		const createdBy = getString(options.createdBy);
		const excludeApproved = options.excludeApproved === true || options.excludeApproved === 'true';

		// Parse comma-separated status values (e.g., 'open,in_progress')
		const statusSet = statusRaw ? new Set(statusRaw.split(',').map((s) => s.trim().toLowerCase())) : null;

		const filtered = rows.filter((wo) => {
			if (associationId && wo.associationId !== associationId) return false;
			if (statusSet && !statusSet.has((wo.status || '').toLowerCase())) return false;
			if (assignedTo && wo.assignedTo !== assignedTo) return false;
			if (createdBy && wo.createdBy !== createdBy) return false;
			if (excludeApproved && wo.pmApproved) return false;
			if (search) {
				const haystack = `${wo.workRequested} ${wo.workCompleted || ''} ${wo.jobLocation || ''}`.toLowerCase();
				if (!haystack.includes(search.toLowerCase())) return false;
			}
			return true;
		});

		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getWorkOrder(id: string): Promise<WorkOrder> {
		await this.ensureSeeded();
		const rows = await this.readCollection('workOrders');
		return this.requireRow(rows, id, 'Work order');
	}

	async createWorkOrder(data: Partial<WorkOrder>): Promise<WorkOrder> {
		await this.ensureSeeded();
		const rows = await this.readCollection('workOrders');

		const session = await authService.getSession();
		if (!session) throw new Error('No active session');

		// Mirror the server-side duplicate guard (api/work-orders.ts) so the
		// "Duplicate Work Order Detected" modal also fires in demo / preview
		// sessions. A matching ack within 10 minutes bypasses the check.
		const DUPLICATE_ACK_MAX_AGE_MS = 10 * 60 * 1000;
		const ackId = (data as any).duplicateAckId as string | undefined;
		const ackTs = (data as any).duplicateAckTimestamp as number | undefined;
		const candidateWorkRequested = data.workRequested ?? '';
		const candidateJobLocation = data.jobLocation ?? '';
		const candidateAssociationId = data.associationId ?? '';
		const candidateCreatedBy = data.createdBy ?? session.id;

		const existingDuplicate = rows.find(wo =>
			!wo.deletedAt
			&& wo.workRequested === candidateWorkRequested
			&& (wo.jobLocation ?? '') === candidateJobLocation
			&& wo.associationId === candidateAssociationId
			&& wo.createdBy === candidateCreatedBy,
		);

		if (existingDuplicate) {
			const ackValid = typeof ackId === 'string'
				&& ackId === existingDuplicate.id
				&& typeof ackTs === 'number'
				&& (Date.now() - ackTs) < DUPLICATE_ACK_MAX_AGE_MS;
			if (!ackValid) {
				throw new DuplicateWorkOrderError(
					existingDuplicate.id,
					existingDuplicate.orderNumber ?? 0,
				);
			}
		}

		const ts = nowIso();
		// Same Date of Order policy as POST /work-orders (#570).
		const actor = (await this.readCollection('users')).find((u) => u.id === session.id);
		const createdAt = resolveCreateCreatedAt(actor, data.createdAt, Date.parse(ts));
		if (!createdAt.ok) throw new Error(createdAt.message);
		const maxOrderNumber = rows.reduce((max, wo) => {
			if (typeof wo.orderNumber === 'number') return Math.max(max, wo.orderNumber);
			return max;
		}, 0);

		const location = await this.getLastKnownLocation();

		const workOrder: WorkOrder = {
			id: data.id || createId(),
			orderNumber: data.orderNumber ?? (maxOrderNumber > 0 ? maxOrderNumber + 1 : 1),
			workRequested: data.workRequested || '',
			workCompleted: data.workCompleted,
			jobLocation: data.jobLocation,
			terms: data.terms,
			status: data.status || WorkOrderStatus.Open,
			priority: data.priority || WorkOrderPriority.Medium,
			dueDate: data.dueDate,
			assignedTo: data.assignedTo,
			associationId: data.associationId || '',
			createdBy: data.createdBy || session.id,
			approvedBy: data.approvedBy,
			pmApproved: data.pmApproved,
			recurrencePeriod: data.recurrencePeriod ?? null,
			recurrenceValue: data.recurrenceValue ?? null,
			recurrenceIntervalMonths: data.recurrenceIntervalMonths ?? null,
			recurrenceParentId: data.recurrenceParentId,
			ownerName: data.ownerName,
			ownerPhone: data.ownerPhone,
			ownerEmail: data.ownerEmail,
			completedAt: data.completedAt,
			estimatedCost: data.estimatedCost,
			actualCost: data.actualCost,
			completionLat: data.completionLat,
			completionLng: data.completionLng,
			latitude: data.latitude ?? location?.latitude,
			longitude: data.longitude ?? location?.longitude,
			savedToServerAt: data.savedToServerAt,
			createdAt: new Date(createdAt.createdAt).toISOString(),
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(workOrder);
		await this.writeCollection('workOrders', rows);
		return workOrder;
	}

	async updateWorkOrder(id: string, data: Partial<WorkOrder>): Promise<WorkOrder> {
		await this.ensureSeeded();
		const rows = await this.readCollection('workOrders');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Work order not found');

		// Same Date of Order policy as PUT /work-orders/:id (#570): gated, and a changed day is
		// stored in the create encoding; anything else leaves the stored value alone.
		const session = await authService.getSession();
		const actor = (await this.readCollection('users')).find((u) => u.id === session?.id);
		const createdAt = resolveEditCreatedAt(actor, data.createdAt, Date.parse(rows[idx].createdAt), Date.now());
		if (!createdAt.ok) throw new Error(createdAt.message);

		const location = await this.getLastKnownLocation();
		const updated: WorkOrder = {
			...rows[idx],
			...data,
			createdAt: createdAt.createdAt === undefined
				? rows[idx].createdAt
				: new Date(createdAt.createdAt).toISOString(),
			latitude: data.latitude ?? rows[idx].latitude ?? location?.latitude,
			longitude: data.longitude ?? rows[idx].longitude ?? location?.longitude,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('workOrders', rows);
		return updated;
	}

	async deleteWorkOrder(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('workOrders');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('workOrders', rows);
	}

	// Associations
	async getAssociations(options: QueryOptions = {}): Promise<PaginatedResponse<Association>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('associations')).filter((a) => !a.deletedAt);
		const search = getString(options.search) ?? getString(options.q);

		const filtered = rows.filter((a) => {
			if (!search) return true;
			const haystack = `${a.name} ${a.address || ''} ${a.city || ''} ${a.state || ''} ${a.zipCode || a.zip || ''}`.toLowerCase();
			return haystack.includes(search.toLowerCase());
		});

		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getAssociation(id: string): Promise<Association> {
		await this.ensureSeeded();
		const rows = await this.readCollection('associations');
		return this.requireRow(rows, id, 'Association');
	}

	async createAssociation(data: Partial<Association>): Promise<Association> {
		await this.ensureSeeded();
		const rows = await this.readCollection('associations');

		const ts = nowIso();
		const association: Association = {
			id: data.id || createId(),
			name: data.name || '',
			address: data.address,
			city: data.city,
			state: data.state,
			zipCode: data.zipCode ?? data.zip,
			zip: data.zip ?? data.zipCode,
			type: data.type || AssociationType.HOA,
			status: data.status || AssociationStatus.Active,
			lat: data.lat ?? data.latitude,
			lng: data.lng ?? data.longitude,
			latitude: data.latitude ?? data.lat,
			longitude: data.longitude ?? data.lng,
			contactName: data.contactName,
			contactEmail: data.contactEmail,
			contactPhone: data.contactPhone,
			notes: data.notes,
			assignedTo: data.assignedTo,
			archivedAt: data.archivedAt,
			numberOfUnits: data.numberOfUnits,
			workOrderCount: data.workOrderCount,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(association);
		await this.writeCollection('associations', rows);
		return association;
	}

	async updateAssociation(id: string, data: Partial<Association>): Promise<Association> {
		await this.ensureSeeded();
		const rows = await this.readCollection('associations');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Association not found');

		const updated: Association = {
			...rows[idx],
			...data,
			zipCode: data.zipCode ?? data.zip ?? rows[idx].zipCode ?? rows[idx].zip,
			zip: data.zip ?? data.zipCode ?? rows[idx].zip ?? rows[idx].zipCode,
			latitude: data.latitude ?? data.lat ?? rows[idx].latitude ?? rows[idx].lat,
			longitude: data.longitude ?? data.lng ?? rows[idx].longitude ?? rows[idx].lng,
			lat: data.lat ?? data.latitude ?? rows[idx].lat ?? rows[idx].latitude,
			lng: data.lng ?? data.longitude ?? rows[idx].lng ?? rows[idx].longitude,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('associations', rows);
		return updated;
	}

	async deleteAssociation(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('associations');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('associations', rows);
	}

	// Association Shortcut Codes
	async getAssociationShortcutCodes(options: QueryOptions = {}): Promise<PaginatedResponse<AssociationShortcutCode>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('associationShortcutCodes')).filter((s) => !s.deletedAt);
		return applyPagination(this.sortRows(rows, options), options);
	}

	async createShortcutCode(data: Partial<AssociationShortcutCode>): Promise<AssociationShortcutCode> {
		await this.ensureSeeded();
		const rows = await this.readCollection('associationShortcutCodes');
		const session = await authService.getSession();
		if (!session) throw new Error('No active session');

		const ts = nowIso();
		const shortcut: AssociationShortcutCode = {
			id: data.id || createId(),
			associationId: data.associationId || '',
			shortcutCode: data.shortcutCode || '',
			fullAddress: data.fullAddress || '',
			city: data.city,
			state: data.state,
			zipCode: data.zipCode ?? data.zip,
			zip: data.zip ?? data.zipCode,
			latitude: data.latitude ?? data.lat,
			longitude: data.longitude ?? data.lng,
			lat: data.lat ?? data.latitude,
			lng: data.lng ?? data.longitude,
			createdBy: data.createdBy || session.id,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(shortcut);
		await this.writeCollection('associationShortcutCodes', rows);
		return shortcut;
	}

	async updateShortcutCode(id: string, data: Partial<AssociationShortcutCode>): Promise<AssociationShortcutCode> {
		await this.ensureSeeded();
		const rows = await this.readCollection('associationShortcutCodes');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Shortcut code not found');

		const updated: AssociationShortcutCode = {
			...rows[idx],
			...data,
			zipCode: data.zipCode ?? data.zip ?? rows[idx].zipCode ?? rows[idx].zip,
			zip: data.zip ?? data.zipCode ?? rows[idx].zip ?? rows[idx].zipCode,
			latitude: data.latitude ?? data.lat ?? rows[idx].latitude ?? rows[idx].lat,
			longitude: data.longitude ?? data.lng ?? rows[idx].longitude ?? rows[idx].lng,
			lat: data.lat ?? data.latitude ?? rows[idx].lat ?? rows[idx].latitude,
			lng: data.lng ?? data.longitude ?? rows[idx].lng ?? rows[idx].longitude,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('associationShortcutCodes', rows);
		return updated;
	}

	async deleteShortcutCode(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('associationShortcutCodes');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('associationShortcutCodes', rows);
	}

	async searchShortcutCodes(query: string, associationId?: string): Promise<AssociationShortcutCode[]> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('associationShortcutCodes')).filter((s) => !s.deletedAt);
		const needle = (query || '').toLowerCase();
		return rows.filter((row) => {
			if (associationId && row.associationId !== associationId) return false;
			if (!needle) return true;
			return row.shortcutCode.toLowerCase().includes(needle) || row.fullAddress.toLowerCase().includes(needle);
		});
	}

	// Users
	async getUsers(options: QueryOptions = {}): Promise<PaginatedResponse<User>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('users')).filter((u) => !u.deletedAt);
		return applyPagination(this.sortRows(rows, options), options);
	}

	async getUser(id: string): Promise<User> {
		await this.ensureSeeded();
		const rows = await this.readCollection('users');
		return this.requireRow(rows, id, 'User');
	}

	async createUser(data: Partial<User>): Promise<User> {
		await this.ensureSeeded();
		const rows = await this.readCollection('users');
		const ts = nowIso();

		const user: User = {
			id: data.id || createId(),
			email: data.email || '',
			name: data.name || '',
			role: data.role || UserRole.PropertyManager,
			isAdmin: data.isAdmin ?? false,
			organizationId: data.organizationId || 'demo',
			hourlyRate: data.hourlyRate,
			overtimeRate: data.overtimeRate,
			onCallRate: data.onCallRate,
			lastLat: data.lastLat,
			lastLng: data.lastLng,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(user);
		await this.writeCollection('users', rows);
		return user;
	}

	async updateUser(id: string, data: Partial<User>): Promise<User> {
		await this.ensureSeeded();
		const rows = await this.readCollection('users');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('User not found');

		const updated: User = {
			...rows[idx],
			...data,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('users', rows);
		return updated;
	}

	async deleteUser(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('users');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('users', rows);
	}

	// Account
	async deleteAccount(): Promise<void> {
		await this.ensureSeeded();
		const prefix = await this.getSessionScopedPrefix();
		const keys = await AsyncStorage.getAllKeys();
		const demoKeys = keys.filter((key) => key.startsWith(prefix));
		if (demoKeys.length > 0) {
			await AsyncStorage.multiRemove(demoKeys);
		}
	}

	// Time Entries
	async getTimeEntries(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<TimeEntry>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('timeEntries')).filter((t) => !t.deletedAt);
		const filtered = workOrderId ? rows.filter((t) => t.workOrderId === workOrderId) : rows;
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getTimeEntry(id: string): Promise<TimeEntry> {
		await this.ensureSeeded();
		const rows = await this.readCollection('timeEntries');
		return this.requireRow(rows, id, 'Time entry');
	}

	async createTimeEntry(data: Partial<TimeEntry>): Promise<TimeEntry> {
		await this.ensureSeeded();
		const rows = await this.readCollection('timeEntries');
		const session = await authService.getSession();
		if (!session) throw new Error('No active session');

		const ts = nowIso();
		const entry: TimeEntry = {
			id: data.id || createId(),
			workOrderId: data.workOrderId || '',
			userId: data.userId || session.id,
			startTime: data.startTime || ts,
			endTime: data.endTime,
			description: data.description,
			hourlyRate: data.hourlyRate,
			rateType: data.rateType,
			hours: data.hours,
			rate: data.rate,
			entryDate: data.entryDate,
			user: data.user,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(entry);
		await this.writeCollection('timeEntries', rows);
		return entry;
	}

	async updateTimeEntry(id: string, data: Partial<TimeEntry>): Promise<TimeEntry> {
		await this.ensureSeeded();
		const rows = await this.readCollection('timeEntries');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Time entry not found');

		const updated: TimeEntry = {
			...rows[idx],
			...data,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('timeEntries', rows);
		return updated;
	}

	async deleteTimeEntry(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('timeEntries');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('timeEntries', rows);
	}

	// Materials
	async getMaterials(options: QueryOptions = {}): Promise<PaginatedResponse<Material>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('materials')).filter((m) => !m.deletedAt);
		const workOrderId = getString(options.workOrderId);
		const filtered = workOrderId ? rows.filter((m) => m.workOrderId === workOrderId) : rows;
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getMaterial(id: string): Promise<Material> {
		await this.ensureSeeded();
		const rows = await this.readCollection('materials');
		return this.requireRow(rows, id, 'Material');
	}

	async createMaterial(data: Partial<Material>): Promise<Material> {
		await this.ensureSeeded();
		const rows = await this.readCollection('materials');
		const ts = nowIso();

		const material: Material = {
			id: data.id || createId(),
			name: data.name || '',
			description: data.description,
			unit: data.unit,
			costPerUnit: data.costPerUnit,
			workOrderId: data.workOrderId,
			materialCostId: data.materialCostId ?? null,
			quantity: data.quantity,
			unitCost: data.unitCost,
			unitType: data.unitType,
			materialCost: data.materialCost,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(material);
		await this.writeCollection('materials', rows);
		return material;
	}

	async updateMaterial(id: string, data: Partial<Material>): Promise<Material> {
		await this.ensureSeeded();
		const rows = await this.readCollection('materials');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Material not found');

		const updated: Material = {
			...rows[idx],
			...data,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('materials', rows);
		return updated;
	}

	async deleteMaterial(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('materials');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('materials', rows);
	}

	// Material Costs
	async getMaterialCosts(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<MaterialCost>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('materialCosts')).filter((m) => !m.deletedAt);
		const filtered = workOrderId ? rows.filter((m) => m.workOrderId === workOrderId) : rows;
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getMaterialCost(id: string): Promise<MaterialCost> {
		await this.ensureSeeded();
		const rows = await this.readCollection('materialCosts');
		return this.requireRow(rows, id, 'Material cost');
	}

	async createMaterialCost(data: Partial<MaterialCost>): Promise<MaterialCost> {
		await this.ensureSeeded();
		const rows = await this.readCollection('materialCosts');
		const ts = nowIso();

		const materialCost: MaterialCost = {
			id: data.id || createId(),
			workOrderId: data.workOrderId || '',
			materialId: data.materialId || '',
			quantity: data.quantity ?? 0,
			costPerUnit: data.costPerUnit ?? 0,
			totalCost: data.totalCost ?? 0,
			description: data.description,
			name: data.name,
			defaultCost: data.defaultCost,
			unitType: data.unitType,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(materialCost);
		await this.writeCollection('materialCosts', rows);
		return materialCost;
	}

	async updateMaterialCost(id: string, data: Partial<MaterialCost>): Promise<MaterialCost> {
		await this.ensureSeeded();
		const rows = await this.readCollection('materialCosts');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Material cost not found');

		const updated: MaterialCost = {
			...rows[idx],
			...data,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('materialCosts', rows);
		return updated;
	}

	async deleteMaterialCost(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('materialCosts');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('materialCosts', rows);
	}

	// Misc Costs
	async getMiscCosts(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<MiscCost>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('miscCosts')).filter((m) => !m.deletedAt);
		const filtered = workOrderId ? rows.filter((m) => m.workOrderId === workOrderId) : rows;
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getMiscCost(id: string): Promise<MiscCost> {
		await this.ensureSeeded();
		const rows = await this.readCollection('miscCosts');
		return this.requireRow(rows, id, 'Misc cost');
	}

	async createMiscCost(data: Partial<MiscCost>): Promise<MiscCost> {
		await this.ensureSeeded();
		const rows = await this.readCollection('miscCosts');
		const ts = nowIso();

		const miscCost: MiscCost = {
			id: data.id || createId(),
			workOrderId: data.workOrderId || '',
			description: data.description || '',
			amount: data.amount ?? 0,
			quantity: data.quantity,
			unitType: data.unitType,
			cost: data.cost,
			totalCost: data.totalCost,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(miscCost);
		await this.writeCollection('miscCosts', rows);
		return miscCost;
	}

	async updateMiscCost(id: string, data: Partial<MiscCost>): Promise<MiscCost> {
		await this.ensureSeeded();
		const rows = await this.readCollection('miscCosts');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Misc cost not found');

		const updated: MiscCost = {
			...rows[idx],
			...data,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('miscCosts', rows);
		return updated;
	}

	async deleteMiscCost(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('miscCosts');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('miscCosts', rows);
	}

	// Notes
	async getNotes(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<Note>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('notes')).filter((n) => !n.deletedAt);
		const filtered = workOrderId ? rows.filter((n) => n.workOrderId === workOrderId) : rows;
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getNote(id: string): Promise<Note> {
		await this.ensureSeeded();
		const rows = await this.readCollection('notes');
		return this.requireRow(rows, id, 'Note');
	}

	async createNote(data: Partial<Note>): Promise<Note> {
		await this.ensureSeeded();
		const rows = await this.readCollection('notes');
		const session = await authService.getSession();
		if (!session) throw new Error('No active session');

		const ts = nowIso();
		const location = await this.getLastKnownLocation();

		const note: Note = {
			id: data.id || createId(),
			workOrderId: data.workOrderId || '',
			userId: data.userId || session.id,
			content: data.content || data.text || '',
			text: data.text ?? data.content,
			createdBy: data.createdBy ?? data.userId ?? session.id,
			createdAtLat: data.createdAtLat ?? location?.latitude,
			createdAtLong: data.createdAtLong ?? location?.longitude,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(note);
		await this.writeCollection('notes', rows);
		return note;
	}

	async updateNote(id: string, data: Partial<Note>): Promise<Note> {
		await this.ensureSeeded();
		const rows = await this.readCollection('notes');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Note not found');

		const updated: Note = {
			...rows[idx],
			...data,
			content: data.content ?? data.text ?? rows[idx].content,
			text: data.text ?? data.content ?? rows[idx].text,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('notes', rows);
		return updated;
	}

	async deleteNote(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('notes');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('notes', rows);
	}

	// Attachments
	async getAttachments(workOrderId?: string, inspectionReportId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<Attachment>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('attachments')).filter((a) => !a.deletedAt);
		const filtered = rows.filter((a) => {
			if (workOrderId && a.workOrderId !== workOrderId) return false;
			if (inspectionReportId && a.inspectionReportId !== inspectionReportId) return false;
			return true;
		});
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getAttachment(id: string): Promise<Attachment> {
		await this.ensureSeeded();
		const rows = await this.readCollection('attachments');
		return this.requireRow(rows, id, 'Attachment');
	}

	async createAttachment(data: Partial<Attachment>): Promise<Attachment> {
		await this.ensureSeeded();
		const rows = await this.readCollection('attachments');
		const ts = nowIso();

		const attachment: Attachment = {
			id: data.id || createId(),
			workOrderId: data.workOrderId,
			fileName: data.fileName || data.name || '',
			fileSize: data.fileSize ?? data.size ?? 0,
			fileType: data.fileType || '',
			description: data.description,
			attachmentType: data.attachmentType || AttachmentType.Misc,
			inspectionReportId: data.inspectionReportId,
			photoTakenAt: data.photoTakenAt,
			photoLat: data.photoLat,
			photoLng: data.photoLng,
			type: data.type ?? data.attachmentType,
			name: data.name ?? data.fileName,
			storageKey: data.storageKey,
			isImage: data.isImage,
			isPdf: data.isPdf,
			uri: data.uri,
			mimeType: data.mimeType,
			size: data.size ?? data.fileSize,
			width: data.width,
			height: data.height,
			imageWidth: data.imageWidth,
			imageHeight: data.imageHeight,
			imageSize: data.imageSize,
			annotations: data.annotations,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(attachment);
		await this.writeCollection('attachments', rows);
		return attachment;
	}

	async updateAttachment(id: string, data: Partial<Attachment>): Promise<Attachment> {
		await this.ensureSeeded();
		const rows = await this.readCollection('attachments');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Attachment not found');

		const updated: Attachment = {
			...rows[idx],
			...data,
			fileName: data.fileName ?? data.name ?? rows[idx].fileName,
			name: data.name ?? data.fileName ?? rows[idx].name,
			fileSize: data.fileSize ?? data.size ?? rows[idx].fileSize,
			size: data.size ?? data.fileSize ?? rows[idx].size,
			attachmentType: data.attachmentType ?? rows[idx].attachmentType,
			type: data.type ?? data.attachmentType ?? rows[idx].type,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('attachments', rows);
		return updated;
	}

	async deleteAttachment(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('attachments');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('attachments', rows);
	}

	async getAttachmentsByWorkOrder(workOrderId: string): Promise<Attachment[]> {
		const response = await this.getAttachments(workOrderId);
		return response.data;
	}

	async getAttachmentsByAssociation(associationId: string): Promise<Attachment[]> {
		await this.ensureSeeded();
		const workOrders = (await this.readCollection('workOrders')).filter((wo) => !wo.deletedAt && wo.associationId === associationId);
		const workOrderIds = new Set(workOrders.map((wo) => wo.id));
		const attachments = (await this.readCollection('attachments')).filter((a) => !a.deletedAt && a.workOrderId && workOrderIds.has(a.workOrderId));
		return attachments;
	}

	async getAttachmentsByInspectionReport(inspectionReportId: string): Promise<Attachment[]> {
		const response = await this.getAttachments(undefined, inspectionReportId);
		return response.data;
	}

	// Location Events
	async getLocationEvents(userId?: string, workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<LocationEvent>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('locationEvents')).filter((e) => !e.deletedAt);
		const filtered = rows.filter((e) => {
			if (userId && e.userId !== userId) return false;
			if (workOrderId && e.workOrderId !== workOrderId) return false;
			return true;
		});
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getLocationEvent(id: string): Promise<LocationEvent> {
		await this.ensureSeeded();
		const rows = await this.readCollection('locationEvents');
		return this.requireRow(rows, id, 'Location event');
	}

	async createLocationEvent(data: Partial<LocationEvent>): Promise<LocationEvent> {
		await this.ensureSeeded();
		const rows = await this.readCollection('locationEvents');
		const session = await authService.getSession();
		if (!session) throw new Error('No active session');

		const ts = nowIso();
		const event: LocationEvent = {
			id: data.id || createId(),
			userId: data.userId || session.id,
			workOrderId: data.workOrderId,
			associationId: data.associationId,
			type: data.type || LocationEventType.CheckIn,
			lat: data.lat ?? data.userLatitude ?? 0,
			lng: data.lng ?? data.userLongitude ?? 0,
			accuracy: data.accuracy,
			timestamp: data.timestamp || ts,
			eventType: data.eventType,
			eventTimestamp: data.eventTimestamp,
			formattedDistance: data.formattedDistance,
			userLatitude: data.userLatitude,
			userLongitude: data.userLongitude,
			association: data.association,
			workOrder: data.workOrder,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(event);
		await this.writeCollection('locationEvents', rows);
		const isCheckIn = event.type === LocationEventType.CheckIn || event.type === LocationEventType.ManualCheckIn
			|| event.eventType === 'checkin';
		const backfilled = isCheckIn && event.userId === session.id && event.associationId
			? await this.backfillAssociationLocation(event.associationId, event.lat, event.lng)
			: false;
		return backfilled ? { ...event, associationLocationBackfilled: true } : event;
	}

	/**
	 * Demo twin of the server's check-in backfill (`api/location-events.ts`): give the association
	 * the check-in's coordinates for whichever of latitude/longitude it is MISSING, never
	 * overwriting one it has. The client no longer PUTs the association itself (#522), so without
	 * this a demo association with no coordinates would never get any.
	 */
	private async backfillAssociationLocation(associationId: string, lat: number, lng: number): Promise<boolean> {
		const rows = await this.readCollection('associations');
		const idx = rows.findIndex((candidate) => candidate.id === associationId && !candidate.deletedAt);
		if (idx < 0) return false;
		const current = rows[idx];
		const hasLat = (current.latitude ?? current.lat) != null;
		const hasLng = (current.longitude ?? current.lng) != null;
		if (hasLat && hasLng) return false;
		rows[idx] = {
			...current,
			...(hasLat ? {} : { latitude: lat, lat }),
			...(hasLng ? {} : { longitude: lng, lng }),
			updatedAt: nowIso(),
		};
		await this.writeCollection('associations', rows);
		return true;
	}

	async updateLocationEvent(id: string, data: Partial<LocationEvent>): Promise<LocationEvent> {
		await this.ensureSeeded();
		const rows = await this.readCollection('locationEvents');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Location event not found');

		const updated: LocationEvent = {
			...rows[idx],
			...data,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('locationEvents', rows);
		return updated;
	}

	async deleteLocationEvent(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('locationEvents');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('locationEvents', rows);
	}

	// Location analytics (not supported in demo mode)
	async getLocationAnalytics(options: QueryOptions = {}): Promise<PaginatedResponse<LocationAnalyticsEntry>> {
		void options;
		return {
			data: [],
			total: 0,
			page: 1,
			limit: 0,
			hasNext: false,
			hasPrev: false,
		};
	}

	// Inspection Reports
	async getInspectionReports(associationId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<InspectionReport>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('inspectionReports')).filter((r) => !r.deletedAt);
		const filtered = associationId ? rows.filter((r) => r.associationId === associationId) : rows;
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getInspectionReport(id: string): Promise<InspectionReport> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionReports');
		return this.requireRow(rows, id, 'Inspection report');
	}

	async createInspectionReport(data: Partial<InspectionReport>): Promise<InspectionReport> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionReports');
		const session = await authService.getSession();
		if (!session) throw new Error('No active session');

		const ts = nowIso();
		const report: InspectionReport = {
			id: data.id || createId(),
			associationId: data.associationId || '',
			userId: data.userId || session.id,
			title: data.title || '',
			description: data.description,
			scheduledDate: data.scheduledDate,
			completedAt: data.completedAt,
			reportNumber: data.reportNumber,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(report);
		await this.writeCollection('inspectionReports', rows);
		return report;
	}

	async updateInspectionReport(id: string, data: Partial<InspectionReport>): Promise<InspectionReport> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionReports');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Inspection report not found');

		const updated: InspectionReport = {
			...rows[idx],
			...data,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('inspectionReports', rows);
		return updated;
	}

	async deleteInspectionReport(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionReports');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('inspectionReports', rows);
	}

	// Inspection Issues
	async getInspectionIssues(options: QueryOptions = {}): Promise<PaginatedResponse<InspectionIssue>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('inspectionIssues')).filter((i) => !i.deletedAt);
		return applyPagination(this.sortRows(rows, options), options);
	}

	async getInspectionIssue(id: string): Promise<InspectionIssue> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionIssues');
		return this.requireRow(rows, id, 'Inspection issue');
	}

	async createInspectionIssue(data: Partial<InspectionIssue>): Promise<InspectionIssue> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionIssues');
		const ts = nowIso();

		const issue: InspectionIssue = {
			id: data.id || createId(),
			title: data.title || data.name || '',
			description: data.description,
			severity: data.severity || 'low',
			category: data.category || 'general',
			name: data.name ?? data.title,
			isActive: data.isActive,
			color: data.color,
			iconName: data.iconName,
			displayIcon: data.displayIcon,
			displayColor: data.displayColor,
			severityColor: data.severityColor,
			severityDisplayName: data.severityDisplayName,
			categoryDisplayName: data.categoryDisplayName,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(issue);
		await this.writeCollection('inspectionIssues', rows);
		return issue;
	}

	async updateInspectionIssue(id: string, data: Partial<InspectionIssue>): Promise<InspectionIssue> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionIssues');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Inspection issue not found');

		const updated: InspectionIssue = {
			...rows[idx],
			...data,
			title: data.title ?? data.name ?? rows[idx].title,
			name: data.name ?? data.title ?? rows[idx].name,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('inspectionIssues', rows);
		return updated;
	}

	async deleteInspectionIssue(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionIssues');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('inspectionIssues', rows);
	}

	// Inspection Report Issues
	async getInspectionReportIssues(inspectionReportId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<InspectionReportIssue>> {
		await this.ensureSeeded();
		const rows = (await this.readCollection('inspectionReportIssues')).filter((r) => !r.deletedAt);
		const filtered = inspectionReportId ? rows.filter((r) => r.inspectionReportId === inspectionReportId) : rows;
		return applyPagination(this.sortRows(filtered, options), options);
	}

	async getInspectionReportIssue(id: string): Promise<InspectionReportIssue> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionReportIssues');
		return this.requireRow(rows, id, 'Inspection report issue');
	}

	async createInspectionReportIssue(data: Partial<InspectionReportIssue>): Promise<InspectionReportIssue> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionReportIssues');
		const ts = nowIso();

		const reportIssue: InspectionReportIssue = {
			id: data.id || createId(),
			inspectionReportId: data.inspectionReportId || '',
			inspectionIssueId: data.inspectionIssueId || '',
			notes: data.notes,
			resolved: data.resolved ?? false,
			resolvedAt: data.resolvedAt,
			createdAt: ts,
			updatedAt: ts,
			deletedAt: undefined,
		};

		rows.push(reportIssue);
		await this.writeCollection('inspectionReportIssues', rows);
		return reportIssue;
	}

	async updateInspectionReportIssue(id: string, data: Partial<InspectionReportIssue>): Promise<InspectionReportIssue> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionReportIssues');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) throw new Error('Inspection report issue not found');

		const updated: InspectionReportIssue = {
			...rows[idx],
			...data,
			updatedAt: nowIso(),
		};

		rows[idx] = updated;
		await this.writeCollection('inspectionReportIssues', rows);
		return updated;
	}

	async deleteInspectionReportIssue(id: string): Promise<void> {
		await this.ensureSeeded();
		const rows = await this.readCollection('inspectionReportIssues');
		const idx = rows.findIndex((candidate) => candidate.id === id && !candidate.deletedAt);
		if (idx < 0) return;
		rows[idx] = { ...rows[idx], deletedAt: nowIso(), updatedAt: nowIso() };
		await this.writeCollection('inspectionReportIssues', rows);
	}

	// Batch operations (simple sequential implementation)
	async batchUpdate<T>(endpoint: string, updates: Array<{ id: string; data: Partial<T> }>): Promise<T[]> {
		await this.ensureSeeded();
		const results: T[] = [];
		for (const update of updates) {
			switch (endpoint) {
				case 'work-orders': {
					const result = await this.updateWorkOrder(update.id, update.data as unknown as Partial<WorkOrder>);
					results.push(result as unknown as T);
					break;
				}
				case 'associations': {
					const result = await this.updateAssociation(update.id, update.data as unknown as Partial<Association>);
					results.push(result as unknown as T);
					break;
				}
				case 'users': {
					const result = await this.updateUser(update.id, update.data as unknown as Partial<User>);
					results.push(result as unknown as T);
					break;
				}
				case 'time-entries': {
					const result = await this.updateTimeEntry(update.id, update.data as unknown as Partial<TimeEntry>);
					results.push(result as unknown as T);
					break;
				}
				case 'materials': {
					const result = await this.updateMaterial(update.id, update.data as unknown as Partial<Material>);
					results.push(result as unknown as T);
					break;
				}
				case 'material-costs': {
					const result = await this.updateMaterialCost(update.id, update.data as unknown as Partial<MaterialCost>);
					results.push(result as unknown as T);
					break;
				}
				case 'misc-costs': {
					const result = await this.updateMiscCost(update.id, update.data as unknown as Partial<MiscCost>);
					results.push(result as unknown as T);
					break;
				}
				case 'notes': {
					const result = await this.updateNote(update.id, update.data as unknown as Partial<Note>);
					results.push(result as unknown as T);
					break;
				}
				case 'attachments': {
					const result = await this.updateAttachment(update.id, update.data as unknown as Partial<Attachment>);
					results.push(result as unknown as T);
					break;
				}
				case 'location-events': {
					const result = await this.updateLocationEvent(update.id, update.data as unknown as Partial<LocationEvent>);
					results.push(result as unknown as T);
					break;
				}
				case 'inspection-reports': {
					const result = await this.updateInspectionReport(update.id, update.data as unknown as Partial<InspectionReport>);
					results.push(result as unknown as T);
					break;
				}
				case 'inspection-issues': {
					const result = await this.updateInspectionIssue(update.id, update.data as unknown as Partial<InspectionIssue>);
					results.push(result as unknown as T);
					break;
				}
				case 'inspection-report-issues': {
					const result = await this.updateInspectionReportIssue(update.id, update.data as unknown as Partial<InspectionReportIssue>);
					results.push(result as unknown as T);
					break;
				}
				default:
					results.push(update.data as unknown as T);
			}
		}
		return results;
	}

	async batchDelete(endpoint: string, ids: string[]): Promise<void> {
		await this.ensureSeeded();
		for (const id of ids) {
			switch (endpoint) {
				case 'work-orders':
					await this.deleteWorkOrder(id);
					break;
				case 'associations':
					await this.deleteAssociation(id);
					break;
				case 'users':
					await this.deleteUser(id);
					break;
				case 'time-entries':
					await this.deleteTimeEntry(id);
					break;
				case 'materials':
					await this.deleteMaterial(id);
					break;
				case 'material-costs':
					await this.deleteMaterialCost(id);
					break;
				case 'misc-costs':
					await this.deleteMiscCost(id);
					break;
				case 'notes':
					await this.deleteNote(id);
					break;
				case 'attachments':
					await this.deleteAttachment(id);
					break;
				case 'location-events':
					await this.deleteLocationEvent(id);
					break;
				case 'inspection-reports':
					await this.deleteInspectionReport(id);
					break;
				case 'inspection-issues':
					await this.deleteInspectionIssue(id);
					break;
				case 'inspection-report-issues':
					await this.deleteInspectionReportIssue(id);
					break;
				default:
					break;
			}
		}
	}

	// Global Search (basic local search over work orders + associations)
	async globalSearch(query: string, options: QueryOptions = {}): Promise<PaginatedResponse<any>> {
		await this.ensureSeeded();
		const needle = (query || '').toLowerCase();

		const workOrders = (await this.readCollection('workOrders')).filter((wo) => !wo.deletedAt);
		const associations = (await this.readCollection('associations')).filter((a) => !a.deletedAt);

		const woMatches = needle
			? workOrders.filter((wo) => `${wo.workRequested} ${wo.workCompleted || ''} ${wo.jobLocation || ''}`.toLowerCase().includes(needle))
			: workOrders;
		const assocMatches = needle
			? associations.filter((a) => `${a.name} ${a.address || ''}`.toLowerCase().includes(needle))
			: associations;

		const combined = [
			...woMatches.map((wo) => ({ type: 'workOrder', item: wo })),
			...assocMatches.map((a) => ({ type: 'association', item: a })),
		];

		return applyPagination(combined, options);
	}

	// Reports (minimal local report)
	async getWorkOrdersReport(params: WorkOrdersReportParams = {}): Promise<WorkOrdersReportResponse> {
		await this.ensureSeeded();
		void params;
		return {
			items: [],
			meta: {
				generatedAt: nowIso(),
				filters: {
					startDate: null,
					endDate: null,
					completedStartDate: null,
					completedEndDate: null,
					status: 'all',
					associationId: null,
				},
				sort: {
					by: 'orderDate',
					direction: 'asc',
				},
			},
		};
	}

	// History (disabled)
	async getHistory(): Promise<{ data: any[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
		return {
			data: [],
			pagination: {
				page: 1,
				limit: 50,
				total: 0,
				pages: 0,
			},
		};
	}

	// Notifications (disabled)
	async sendNotificationToUser(
		userId: string,
		title: string,
		body: string,
		data?: {
			type?: 'update' | 'workorder' | 'general';
			workOrderId?: string;
			action?: 'testflight' | 'workorder' | 'modal';
			url?: string;
			[key: string]: unknown;
		},
	): Promise<any> {
		void userId;
		void title;
		void body;
		void data;
		return { message: 'Demo mode - notifications disabled' };
	}

	// File Storage for Demo Mode
	private async getFileStorageKey(storageKey: string): Promise<string> {
		const prefix = await this.getSessionScopedPrefix();
		return `${prefix}${LOCAL_FILES_PREFIX}${storageKey}`;
	}

	async storeFileContent(storageKey: string, base64Content: string): Promise<void> {
		const key = await this.getFileStorageKey(storageKey);
		await AsyncStorage.setItem(key, base64Content);
	}

	async getFileContent(storageKey: string): Promise<string | null> {
		const key = await this.getFileStorageKey(storageKey);
		return AsyncStorage.getItem(key);
	}

	async deleteFileContent(storageKey: string): Promise<void> {
		const key = await this.getFileStorageKey(storageKey);
		await AsyncStorage.removeItem(key);
	}

	/**
	 * Upload a file locally in demo mode
	 * Stores the file content and creates an attachment record
	 */
	async uploadFile(formData: FormData): Promise<{
		success: boolean;
		storageKey: string;
		fileName: string;
		fileSize: number;
		fileType: string;
		mimeType: string;
		isImage: boolean;
		isPdf: boolean;
		imageWidth?: number;
		imageHeight?: number;
	}> {
		await this.ensureSeeded();

		const file = getFormDataValue(formData, 'file') as any;
		const fileNameFromForm = getFormDataValue(formData, 'fileName') as string | null;
		const attachmentType = getFormDataValue(formData, 'attachmentType') as string | null;
		const workOrderId = getFormDataValue(formData, 'workOrderId') as string | null;
		const associationId = getFormDataValue(formData, 'associationId') as string | null;
		const inspectionReportId = getFormDataValue(formData, 'inspectionReportId') as string | null;

		if (!file) {
			throw new Error('No file provided');
		}

		// Generate storage key
		const storageKey = `local-${createId()}`;

		// Get file info - handle both web File/Blob and React Native file objects
		const fileName = fileNameFromForm || file.name || file.fileName || 'untitled';
		const fileSize = file.size || file.fileSize || 0;
		const mimeType = file.type || file.mimeType || 'application/octet-stream';
		const fileType = fileName.split('.').pop()?.toLowerCase() || '';

		// Check if image/PDF
		const isImage = mimeType.startsWith('image/');
		const isPdf = mimeType === 'application/pdf' || fileType === 'pdf';

		let dataUrl: string;

		// Handle different file types
		if (typeof file.arrayBuffer === 'function') {
			// Web File/Blob with arrayBuffer method
			const arrayBuffer = await file.arrayBuffer();
			const base64 = btoa(
				new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
			);
			dataUrl = `data:${mimeType};base64,${base64}`;
		} else if (file.uri) {
			// React Native file object with URI - use expo-file-system legacy API
			try {
				const ExpoFileSystemLegacy = await import('expo-file-system/legacy');
				const base64Content = await ExpoFileSystemLegacy.readAsStringAsync(file.uri, {
					encoding: ExpoFileSystemLegacy.EncodingType.Base64,
				});
				dataUrl = `data:${mimeType};base64,${base64Content}`;
			} catch (error) {
				console.error('DemoDataService: failed to read file from URI', error);
				// Fallback: store the URI directly (won't work for display but prevents crash)
				dataUrl = file.uri;
			}
		} else {
			throw new Error('Unsupported file format - no arrayBuffer or uri available');
		}

		await this.storeFileContent(storageKey, dataUrl);

		// Get image dimensions if applicable
		let imageWidth: number | undefined;
		let imageHeight: number | undefined;
		if (isImage) {
			// Try to get dimensions from file object first (React Native often includes these)
			if (file.width && file.height) {
				imageWidth = file.width;
				imageHeight = file.height;
			} else if (typeof Image !== 'undefined' && dataUrl.startsWith('data:')) {
				try {
					const dimensions = await this.getImageDimensions(dataUrl);
					imageWidth = dimensions.width;
					imageHeight = dimensions.height;
				} catch (error) {
					console.warn('DemoDataService: failed to get image dimensions', error);
				}
			}
		}

		// Create attachment record
		// Note: associationId is not stored on Attachment - attachments are linked
		// to associations through their workOrderId
		const attachmentData: Partial<Attachment> = {
			storageKey,
			fileName,
			fileSize,
			fileType,
			mimeType,
			isImage,
			isPdf,
			imageWidth,
			imageHeight,
			attachmentType: (attachmentType as AttachmentType) || AttachmentType.Misc,
			workOrderId: workOrderId || undefined,
			inspectionReportId: inspectionReportId || undefined,
		};

		// We still extract associationId from form data but don't store it on the attachment
		void associationId;

		await this.createAttachment(attachmentData);

		return {
			success: true,
			storageKey,
			fileName,
			fileSize,
			fileType,
			mimeType,
			isImage,
			isPdf,
			imageWidth,
			imageHeight,
		};
	}

	private getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
		return new Promise((resolve, reject) => {
			if (typeof Image === 'undefined') {
				reject(new Error('Image not available'));
				return;
			}
			const img = new Image();
			img.onload = () => {
				resolve({ width: img.width, height: img.height });
			};
			img.onerror = () => {
				reject(new Error('Failed to load image'));
			};
			img.src = dataUrl;
		});
	}

	/**
	 * Download a file from local storage in demo mode
	 * Returns a Response-like object with the file content
	 */
	async downloadFile(storageKey: string): Promise<Response> {
		const content = await this.getFileContent(storageKey);

		if (!content) {
			return new Response('File not found', { status: 404 });
		}

		// Parse data URL to get mime type and base64 data
		const match = content.match(/^data:([^;]+);base64,(.+)$/);
		if (!match) {
			return new Response('Invalid file format', { status: 500 });
		}

		const mimeType = match[1];
		const base64Data = match[2];

		// Convert base64 to Uint8Array
		const binaryString = atob(base64Data);
		const bytes = new Uint8Array(binaryString.length);
		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}

		// Create a Blob from the bytes
		const blob = new Blob([bytes], { type: mimeType });

		// Create a mock Response that works in React Native
		// React Native's Response implementation can be quirky with binary data
		const mockResponse = {
			ok: true,
			status: 200,
			statusText: 'OK',
			headers: new Headers({
				'Content-Type': mimeType,
				'Content-Length': String(bytes.length),
			}),
			redirected: false,
			type: 'basic' as ResponseType,
			url: '',
			body: null,
			bodyUsed: false,
			blob: async () => blob,
			text: async () => content,
			json: async () => { throw new Error('Not JSON'); },
			arrayBuffer: async () => bytes.buffer,
			formData: async () => { throw new Error('Not FormData'); },
			clone: () => mockResponse,
		} as unknown as Response;

		return mockResponse;
	}
}

export const demoDataService = new DemoDataService();

